import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  beginTwoFactorSetup,
  recoverTwoFactor,
  verifyTwoFactorCode,
} from "../api/authApi";
import PasswordInput from "../components/PasswordInput";
import TwoFactorQrCode from "../components/TwoFactorQrCode";
import SixDigitCodeInput from "../components/SixDigitCodeInput";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, completeTwoFactorLogin } = useAuth();
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [recoveryPhrase, setRecoveryPhrase] = useState("");
  const [setup, setSetup] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  const [recoveryForDisplay, setRecoveryForDisplay] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const goToDestination = () => {
    const destination = location.state?.from;
    navigate(
      destination
        ? `${destination.pathname || "/"}${destination.search || ""}${
            destination.hash || ""
          }`
        : "/",
      { replace: true },
    );
  };

  const startEnrollment = async (user = null) => {
    const setupData = await beginTwoFactorSetup();
    setPendingUser(user);
    setSetup(setupData);
    setCode("");
    setStep("setup");
  };

  const finishAuthentication = (user) => {
    if (user?.twoFactorRequired && !user?.twoFactorEnabled) {
      startEnrollment(user).catch((requestError) => {
        setError(
          requestError?.detail ||
            "Could not start authenticator setup. Please try again.",
        );
      });
      return;
    }

    if (user?.recoveryPhrase) {
      setPendingUser(user);
      setRecoveryForDisplay(user.recoveryPhrase);
      setStep("recoveryPhrase");
      return;
    }

    completeTwoFactorLogin(user);
    goToDestination();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      if (step === "credentials") {
        const result = await login(email, password);
        if (result?.twoFactorRequired) {
          if (result.enrollmentRequired) {
            await startEnrollment();
          } else {
            setStep("authenticator");
          }
        } else {
          goToDestination();
        }
      } else if (step === "setup") {
        const result = await verifyTwoFactorCode(code, "enable");
        const authenticatedUser = result.userId
          ? result
          : {
              ...pendingUser,
              twoFactorEnabled: true,
              recoveryPhrase: result.recoveryPhrase,
            };
        finishAuthentication(authenticatedUser);
      } else if (step === "authenticator") {
        const result = await verifyTwoFactorCode(code);
        finishAuthentication(result);
      } else if (step === "recovery") {
        const result = await recoverTwoFactor(recoveryPhrase);
        finishAuthentication(result);
      }
    } catch (requestError) {
      const fallbackMessage =
        step === "credentials"
          ? "Login could not be completed. Please check your credentials or try again."
          : "That code or recovery phrase could not be verified.";
      setError(
        requestError?.status
          ? requestError.detail || fallbackMessage
          : "The backend is unavailable. Please try again later.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const continueAfterRecoveryPhrase = () => {
    const user = { ...pendingUser };
    delete user.recoveryPhrase;
    completeTwoFactorLogin(user);
    goToDestination();
  };

  const title = {
    credentials: "Welcome back",
    authenticator: "Verify your identity",
    recovery: "Use your recovery phrase",
    setup: "Set up an authenticator",
    recoveryPhrase: "Save your recovery phrase",
  }[step];

  return (
    <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-4 py-10 sm:px-6">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-xl sm:grid-cols-[0.9fr_1.1fr]">
        <div className="flex items-center justify-center border-b border-zinc-200 bg-zinc-50 p-8 sm:min-h-[28rem] sm:border-b-0 sm:border-r">
          <img
            src="/logos/J-Starter.png"
            alt="J-Starter"
            className="h-auto max-h-48 w-full max-w-64 object-contain"
          />
        </div>

        <section className="space-y-5 p-7 sm:p-10">
          <div className="mb-8">
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-blue-700">
              J-Starter Portal
            </p>
            <h1 className="text-3xl font-semibold tracking-tight text-zinc-950">
              {title}
            </h1>
            <p className="mt-2 text-sm text-zinc-600">
              {step === "credentials" &&
                "Sign in to continue to your workspace."}
              {step === "authenticator" &&
                "Enter the six-digit code from your authenticator app."}
              {step === "recovery" &&
                "Enter the one-time recovery phrase you saved during setup."}
              {step === "setup" &&
                "Add this account to your authenticator app, then verify the six-digit code."}
              {step === "recoveryPhrase" &&
                "This phrase is shown once. Save it somewhere safe before continuing."}
            </p>
          </div>

          {location.state?.sessionExpired && step === "credentials" && (
            <p
              className="rounded-md bg-amber-100 p-3 text-sm text-amber-900"
              role="status"
            >
              Your session has ended. Please log in again.
            </p>
          )}

          {step === "recoveryPhrase" ? (
            <div className="space-y-5">
              <p
                className="rounded-md border border-amber-300 bg-amber-50 p-4 font-mono text-sm font-semibold tracking-wide text-zinc-900"
                role="alert"
              >
                {recoveryForDisplay}
              </p>
              <button
                type="button"
                onClick={continueAfterRecoveryPhrase}
                className="w-full rounded-md bg-blue-800 px-4 py-2.5 font-medium text-white transition-colors hover:bg-blue-900"
              >
                I saved my recovery phrase
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {step === "credentials" && (
                <>
                  <div className="space-y-2">
                    <label className="block" htmlFor="email">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block" htmlFor="password">
                      Password
                    </label>
                    <PasswordInput
                      id="password"
                      name="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </>
              )}

              {step === "setup" && setup && (
                <div className="space-y-3 rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm">
                  {process.env.REACT_APP_MOCK_API === "true" ? (
                    <p className="text-amber-800">
                      Dev mock uses the fixed code 123456. Its sample seed does
                      not generate real authenticator codes.
                    </p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <p className="font-medium text-zinc-900">
                          Scan this QR code with your authenticator app
                        </p>
                        <TwoFactorQrCode
                          provisioningUri={setup.provisioningUri}
                        />
                      </div>
                      <div className="space-y-2">
                        <p className="font-medium text-zinc-900">
                          Or enter the key manually
                        </p>
                        <p className="text-zinc-600">
                          Add a time-based account with this key:
                        </p>
                        <code className="block break-all rounded bg-white p-3 font-mono text-base font-semibold text-zinc-900">
                          {setup.secret}
                        </code>
                        <details>
                          <summary className="cursor-pointer text-blue-800">
                            Show setup link
                          </summary>
                          <code className="mt-2 block break-all rounded bg-white p-3 text-xs text-zinc-700">
                            {setup.provisioningUri}
                          </code>
                        </details>
                      </div>
                    </>
                  )}
                </div>
              )}

              {(step === "authenticator" || step === "setup") && (
                <div className="space-y-2">
                  <SixDigitCodeInput
                    idPrefix="totp-code"
                    label={
                      step === "setup"
                        ? "Authenticator setup verification code"
                        : "Authenticator sign-in code"
                    }
                    value={code}
                    onChange={setCode}
                    autoFocus
                  />
                  {process.env.REACT_APP_MOCK_API === "true" && (
                    <p className="text-sm text-amber-800">
                      Dev mock TOTP code: 123456
                    </p>
                  )}
                </div>
              )}

              {step === "recovery" && (
                <div className="space-y-2">
                  <label className="block" htmlFor="recovery-phrase">
                    Recovery phrase
                  </label>
                  <input
                    id="recovery-phrase"
                    name="recovery-phrase"
                    type="text"
                    autoComplete="off"
                    required
                    value={recoveryPhrase}
                    onChange={(event) =>
                      setRecoveryPhrase(event.target.value.trim())
                    }
                    className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 font-mono focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              )}

              {error && (
                <p
                  className="rounded-md bg-red-100 p-3 text-sm text-red-800"
                  role="alert"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={
                  submitting ||
                  ((step === "authenticator" || step === "setup") &&
                    code.length !== 6)
                }
                className="w-full rounded-md bg-blue-800 px-4 py-2.5 font-medium text-white transition-colors hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting
                  ? "Please wait…"
                  : step === "credentials"
                  ? "Log in"
                  : step === "setup"
                  ? "Verify and enable 2FA"
                  : step === "recovery"
                  ? "Use recovery phrase"
                  : "Verify code"}
              </button>

              {step === "authenticator" && (
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setStep("recovery");
                  }}
                  className="w-full text-sm font-medium text-blue-800 hover:underline"
                >
                  Use a recovery phrase instead
                </button>
              )}
              {step !== "credentials" && step !== "setup" && (
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setStep("credentials");
                  }}
                  className="w-full text-sm text-zinc-600 hover:underline"
                >
                  Back to login
                </button>
              )}
            </form>
          )}
        </section>
      </div>
    </main>
  );
};

export default LoginPage;
